import { useEffect, useMemo, useState } from "react";
import api, { fileUrl, errMsg } from "../lib/api";
import { ACTIVITY_TYPES, fmtDate } from "../lib/constants";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { CallTranscript } from "./CallTranscript";
import { ReminderList } from "./ReminderList";

export const fmtDur = (s) => {
  const secs = Math.max(0, Math.round(Number(s) || 0));
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;
};

const ACT_ICON = { call_pickup: "PhoneCall", call_no_answer: "PhoneMissed", email_sent: "Mail", sms_sent: "MessageSquare", sms_received: "MessageSquare", note: "StickyNote", stage_change: "GitBranch", created: "Sparkles", quote_created: "FileText", quote_viewed: "Eye", quote_signed: "FileSignature", document_uploaded: "Upload", lost: "XCircle", won: "Trophy" };

const CALL_TONE = { answered: "bg-emerald-100 text-emerald-800", no_answer: "bg-red-100 text-red-700", busy: "bg-amber-100 text-amber-800", failed: "bg-red-100 text-red-700", voicemail: "bg-blue-100 text-blue-700" };

const TemplatePicker = ({ channel, onPick }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/message-templates?channel=${channel}`).then((r) => setItems(r.data)).catch(() => {}); }, [channel]);
  return (
    <select onChange={(e) => { const t = items.find((x) => x.id === e.target.value); if (t) onPick(t); e.target.value = ""; }}
      defaultValue="" data-testid={`${channel}-template-select`} className="px-3 py-2 rounded-md border border-input bg-background text-sm max-w-[220px]">
      <option value="">{items.length ? `Use ${channel === "sms" ? "SMS" : "email"} template…` : "No saved templates"}</option>
      {items.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
    </select>
  );
};

export default function ActivityHub({ lead, activities, comms, commsCfg, onRefresh, onCall, calling }) {
  const [mode, setMode] = useState("note");
  const [tag, setTag] = useState("");
  const [note, setNote] = useState("");
  const [sms, setSms] = useState("");
  const [subject, setSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [reminderAt, setReminderAt] = useState("");

  const personalize = (t) => (t || "").replace(/{{\s*first_name\s*}}/gi, lead.first_name || "").replace(/{{\s*full_name\s*}}/gi, `${lead.first_name || ""} ${lead.last_name || ""}`.trim());

  const timeline = useMemo(() => {
    const items = comms.map((c) => ({ kind: "comm", at: c.created_at, data: c, id: `c-${c.id}` }));
    const commIds = new Set(comms.map((c) => c.id));
    const callBodies = comms.filter((c) => c.channel === "call").map((c) => c.body);
    for (const a of activities) {
      if (a.comm_id && commIds.has(a.comm_id)) continue;
      if (["sms_sent", "sms_received"].includes(a.type) && comms.some((c) => c.channel === "sms" && Math.abs(new Date(c.created_at) - new Date(a.created_at)) < 120000)) continue;
      if (a.type?.startsWith("call") && callBodies.includes(a.message)) continue;
      if (a.type === "email_sent" && comms.some((c) => c.channel === "email" && Math.abs(new Date(c.created_at) - new Date(a.created_at)) < 120000)) continue;
      items.push({ kind: "act", at: a.created_at, data: a, id: `a-${a.id}` });
    }
    return items.sort((x, y) => (y.at || "").localeCompare(x.at || ""));
  }, [activities, comms]);

  const logNote = async () => {
    if (!tag && !note.trim() && !reminderAt) return toast.error("Pick a tag or write a note");
    setBusy(true);
    try {
      const label = ACTIVITY_TYPES.find((a) => a.key === tag)?.label;
      const message = tag && note.trim() ? `${label} — ${note.trim()}` : (note.trim() || label || "Follow-up reminder");
      await api.post("/activities", { lead_id: lead.id, type: tag || "note", message, reminder_at: reminderAt || null });
      setNote(""); setTag(""); setReminderAt(""); onRefresh(); toast.success("Activity logged");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const sendSms = async () => {
    if (!sms.trim()) return;
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/sms`, { body: sms });
      setSms(""); onRefresh();
      toast.success((commsCfg?.configured || commsCfg?.sms_enabled) ? "SMS sent" : "SMS logged (demo — connect Uniden to send)");
    } catch (e) { toast.error(errMsg(e, "Failed to send SMS")); } finally { setBusy(false); }
  };
  const sendEmail = async () => {
    if (!subject.trim() || !emailBody.trim()) return toast.error("Subject and message are required");
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/send-email`, { subject, body: emailBody });
      setSubject(""); setEmailBody(""); onRefresh(); toast.success("Email sent");
    } catch (e) { toast.error(errMsg(e, "Failed to send email")); } finally { setBusy(false); }
  };

  const segs = Math.ceil(sms.length / 160) || 0;
  const modes = [["note", "StickyNote", "Log activity"], ["sms", "MessageSquare", "Send SMS"], ["email", "Mail", "Send Email"]];

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="activity-hub">
      <div className="lg:col-span-2 space-y-3">
        <div className="bg-card border border-border rounded-md p-4 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="p-2.5 rounded-full bg-secondary/10 text-secondary"><Icons.Phone size={18} /></span>
            <div>
              <div className="font-head font-bold text-lg">{lead.phone || "No phone number"}</div>
              <div className="text-xs text-muted-foreground">{lead.email || "No email"}</div>
            </div>
          </div>
          <button onClick={onCall} disabled={calling || !lead.phone} data-testid="click-to-call"
            className="px-5 py-2.5 rounded-md bg-primary text-primary-foreground font-semibold flex items-center gap-2 disabled:opacity-60">
            {calling ? <Icons.Loader2 size={16} className="animate-spin" /> : <Icons.PhoneCall size={16} />} Call
          </button>
        </div>

        <div className="bg-card border border-border rounded-md shadow-sm">
          <div className="flex border-b border-border">
            {modes.map(([k, ic, lbl]) => { const I = Icons[ic]; return (
              <button key={k} onClick={() => setMode(k)} data-testid={`composer-${k}`}
                className={`min-w-0 flex-1 flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-4 py-2.5 text-xs sm:text-sm font-medium border-b-2 -mb-px transition-colors ${mode === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                <I size={15} className="shrink-0" /> {lbl}</button>); })}
          </div>
          <div className="p-4 space-y-3">
            {mode === "note" && (<>
              <div className="flex flex-wrap gap-2">
                {ACTIVITY_TYPES.filter((a) => a.key !== "note").map((a) => (
                  <button key={a.key} onClick={() => setTag(tag === a.key ? "" : a.key)} data-testid={`log-${a.key}`}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-all ${tag === a.key ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>{a.label}</button>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={tag ? "Add notes for this activity…" : "Add a note…"} data-testid="note-input"
                  onKeyDown={(e) => { if (e.key === "Enter") logNote(); }}
                  className="flex-1 px-3 py-2 rounded-md border border-input bg-background" />
                <button onClick={logNote} disabled={busy} data-testid="log-activity-btn" className="px-4 py-2 rounded-md bg-secondary text-secondary-foreground font-semibold disabled:opacity-60">Log</button>
              </div>
              <label className="block text-xs text-muted-foreground">Reminder · Australia/Melbourne
                <input type="datetime-local" value={reminderAt} onChange={(e) => setReminderAt(e.target.value)} data-testid="activity-reminder-at"
                  className="block w-full min-w-0 mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm" />
              </label>
            </>)}
            {mode === "sms" && (<>
              <div className="flex flex-wrap gap-2 items-center">
                <TemplatePicker channel="sms" onPick={(t) => setSms(personalize(t.body))} />
                <span className="text-xs text-muted-foreground ml-auto" data-testid="sms-char-count">{sms.length} chars · {segs} segment{segs === 1 ? "" : "s"}</span>
              </div>
              <textarea value={sms} onChange={(e) => setSms(e.target.value)} rows={3} placeholder="Type an SMS to this lead…" data-testid="sms-input"
                className="w-full px-3 py-2 rounded-md border border-input bg-background resize-none" />
              <div className="flex justify-end">
                <button onClick={sendSms} disabled={busy || !sms.trim() || !lead.phone} data-testid="send-sms"
                  className="px-5 py-2 rounded-md bg-secondary text-secondary-foreground font-semibold flex items-center gap-2 disabled:opacity-60">
                  {busy ? <Icons.Loader2 size={16} className="animate-spin" /> : <Icons.Send size={16} />} Send SMS</button>
              </div>
            </>)}
            {mode === "email" && (<>
              <div className="flex flex-wrap gap-2 items-center">
                <TemplatePicker channel="email" onPick={(t) => { setSubject(personalize(t.subject)); setEmailBody(personalize(t.body)); }} />
                <span className="text-xs text-muted-foreground ml-auto">To: {lead.email || "— no email on lead —"}</span>
              </div>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" data-testid="email-subject"
                className="w-full px-3 py-2 rounded-md border border-input bg-background" />
              <textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={5} placeholder="Write your email…" data-testid="email-body"
                className="w-full px-3 py-2 rounded-md border border-input bg-background resize-y" />
              <div className="flex justify-end">
                <button onClick={sendEmail} disabled={busy || !lead.email} data-testid="send-email"
                  className="px-5 py-2 rounded-md bg-secondary text-secondary-foreground font-semibold flex items-center gap-2 disabled:opacity-60">
                  {busy ? <Icons.Loader2 size={16} className="animate-spin" /> : <Icons.Mail size={16} />} Send Email</button>
              </div>
            </>)}
          </div>
        </div>

        <div className="space-y-2" data-testid="activity-list">
          {timeline.length === 0 && <p className="text-sm text-muted-foreground text-center py-10">No activity yet.</p>}
          {timeline.map((it) => it.kind === "comm" ? <CommItem key={it.id} c={it.data} /> : <ActItem key={it.id} a={it.data} />)}
        </div>
      </div>
      <div className="min-w-0 h-fit"><ReminderList leadId={lead.id} refreshKey={activities.length} /></div>
    </div>
  );
}

const ActItem = ({ a }) => {
  const Icon = Icons[ACT_ICON[a.type]] || Icons.Circle;
  return (
    <div className="flex gap-3 bg-card border border-border rounded-md p-3" data-testid="activity-item">
      <span className="p-2 rounded-md bg-muted h-fit"><Icon size={15} /></span>
      <div className="flex-1 min-w-0">
        <div className="text-sm whitespace-pre-wrap">{a.message || ACTIVITY_TYPES.find((x) => x.key === a.type)?.label || a.type}</div>
        <div className="text-xs text-muted-foreground">{a.user_name} · {fmtDate(a.created_at)}</div>
      </div>
    </div>
  );
};

const CommItem = ({ c }) => {
  const out = c.direction === "outbound";
  if (c.channel === "call") {
    const tone = CALL_TONE[c.status] || "bg-muted text-muted-foreground";
    return (
      <div className="flex gap-3 bg-card border border-border rounded-md p-3" data-testid="call-item">
        <span className={`p-2 rounded-md h-fit ${tone}`}>{out ? <Icons.PhoneOutgoing size={15} /> : <Icons.PhoneIncoming size={15} />}</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium flex flex-wrap items-center gap-2">
            {out ? "Outgoing call" : "Incoming call"}
            <span className={`text-xs px-2 py-0.5 rounded-full ${tone}`} data-testid="call-status">{({ answered: "Answered", no_answer: "Not answered", busy: "Busy", failed: "Failed", voicemail: "Voicemail", ringing: "Ringing…", dialed: "Dialing…", completed: "Answered" })[c.status] || c.status}</span>
            {(c.status === "answered" || c.status === "completed" || c.duration > 0) && <span className="text-xs text-muted-foreground" data-testid="call-duration">{fmtDur(c.duration)}</span>}
          </div>
          <div className="text-xs text-muted-foreground">{c.user_name || "VOCPhone"} · {fmtDate(c.created_at)}</div>
          {c.recording_url && <audio controls preload="none" src={c.recording_file_id ? fileUrl(c.recording_file_id) : c.recording_url} data-testid="call-recording" className="h-9 max-w-full mt-2" />}
          <CallTranscript call={c} />
        </div>
      </div>
    );
  }
  if (c.channel === "email") {
    return (
      <div className="flex gap-3 bg-card border border-border rounded-md p-3" data-testid="email-item">
        <span className="p-2 rounded-md bg-muted h-fit"><Icons.Mail size={15} /></span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">{c.subject || "Email"}</div>
          <div className="text-xs whitespace-pre-wrap text-muted-foreground line-clamp-3">{c.body}</div>
          <div className="text-xs text-muted-foreground mt-1">{c.user_name} → {c.to_number} · {fmtDate(c.created_at)}</div>
        </div>
      </div>
    );
  }
  return (
    <div className={`flex ${out ? "justify-end" : "justify-start"}`} data-testid="sms-item">
      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${out ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-muted text-foreground rounded-bl-sm"}`}>
        <div className="text-sm whitespace-pre-wrap break-words">{c.body}</div>
        <div className={`text-[10px] mt-1 ${out ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
          {out ? (c.user_name || "You") : "Customer"} · SMS · {fmtDate(c.created_at)}{c.status === "demo" && " · demo"}
        </div>
      </div>
    </div>
  );
};
